'use client'

import { useCallback, useMemo } from 'react'

import { cn } from '../../../utils/cn'
import { Button } from '../Button'

type CategoryOptionLike = {
  value: string
  category?: string | null
}

interface CategoryControlsProps<TOption extends CategoryOptionLike> {
  options: TOption[]
  values: string[]
  formatCategoryLabel?: (categoryId: string) => string
  onSelect: (values: string[]) => void
  onSortingCategoryChange: (category: string | null) => void
}

const activeCategoryClassName =
  'border-[0.5px] border-border bg-popover shadow-sm hover:bg-accent active:bg-accent dark:border-transparent dark:bg-secondary dark:shadow-none dark:hover:bg-secondary dark:active:bg-secondary/80'

export function CategoryControls<TOption extends CategoryOptionLike>({
  options,
  values,
  formatCategoryLabel,
  onSelect,
  onSortingCategoryChange
}: CategoryControlsProps<TOption>) {
  const categoryMap = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const option of options) {
      const category = option.category
      if (category == null) continue

      const set = map.get(category) ?? new Set<string>()
      set.add(option.value)
      map.set(category, set)
    }
    return map
  }, [options])

  const resetSelection = useCallback(() => {
    onSelect([])
    onSortingCategoryChange(null)
  }, [onSelect, onSortingCategoryChange])

  const activeCategory = useMemo(() => {
    if (values.length === 0) return 'all'

    const selectedSet = new Set(values)
    for (const [category, categorySet] of categoryMap) {
      if (categorySet.size !== selectedSet.size) continue

      let matches = true
      for (const val of selectedSet) {
        if (!categorySet.has(val)) {
          matches = false
          break
        }
      }

      if (matches) return category
    }

    return null
  }, [values, categoryMap])

  const handleCategoryChange = useCallback(
    (category: string) => {
      if (category === 'all') return resetSelection()

      const categorySet = categoryMap.get(category)
      if (!categorySet) return

      const isCurrentlyActive = activeCategory === category

      if (isCurrentlyActive) return resetSelection()

      onSelect([...categorySet])
      onSortingCategoryChange(category)
    },
    [categoryMap, activeCategory, resetSelection, onSelect, onSortingCategoryChange]
  )

  if (categoryMap.size === 0) return null

  return (
    <div className="gap-2xs flex flex-1 items-center">
      <button
        type="button"
        onClick={() => handleCategoryChange('all')}
        className={cn(
          'text-foreground text-3xs border-border cursor-pointer rounded-lg border px-2.5 py-1',
          'hover:bg-secondary/50 active:bg-secondary/70',
          activeCategory === 'all' && activeCategoryClassName
        )}
      >
        All
      </button>
      {Array.from(categoryMap.keys()).map(category => (
        <button
          key={category}
          type="button"
          onClick={() => handleCategoryChange(category)}
          className={cn(
            'text-foreground text-3xs border-border cursor-pointer rounded-lg border px-2.5 py-1 capitalize',
            'hover:bg-secondary/50 active:bg-secondary/70',
            activeCategory === category && activeCategoryClassName
          )}
        >
          {formatCategoryLabel?.(category) ?? category}
        </button>
      ))}
      <Button
        disabled={values.length === 0}
        onClick={resetSelection}
        variant="ghost"
        size="sm"
        className="ml-auto"
      >
        Clear
      </Button>
    </div>
  )
}
